import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { get, map, keys, isEmpty, includes } from 'lodash'

import { MENU_TYPES } from '../../utils/menuTypes'
import Modal from '../Modal'
import ToggleWithLabel from '../ToggleWithLabel'
import DropdownMenu from '../DropdownMenu'
import CheckboxGroupItem from '../CheckboxGroupItem'

const resolveTitle = (key) => {
  return MENU_TYPES[key] || key
}

const resolveGratuity = (gratuity, isPercentage) => {
  if (!gratuity) {
    return 'none'
  } else {
    return isPercentage ? `${gratuity}%` : `$${(gratuity / 100).toFixed(2)}`
  }
}

const resolveMaxGratuity = (maxGratuity) => {
  if (!maxGratuity) {
    return 'none'
  } else {
    return `$${(maxGratuity / 100).toFixed(2)}`
  }
}

const DefaultPreferencesViewModal = (preference, customerPayments) => {
  const menuTypes = keys(MENU_TYPES)

  const makeMenuTypeSettings = (key) => {
    const title = resolveTitle(key)
    const menuType = preference[key]

    const adjustable = get(menuType, 'gratuityIsAdjustable', true)
    const gratuityIsPercentage = get(menuType, 'gratuityIsPercentage', true)
    const gratuity = resolveGratuity(get(menuType, 'gratuity'), gratuityIsPercentage)
    const maxGratuity = resolveMaxGratuity(get(menuType, 'maxGratuity', 0))

    let modalData = [
      <div className="settings-row" key={key}>
        <span className="title">Payments</span>
        <span className="title">Signer(s)</span>
      </div>
    ]

    if (customerPayments.length) {
        modalData = modalData.concat([
          <div className="settings-column" key={key}>
            {customerPayments.map(customerPayment => {
              const tooltipText = customerPayment.authorizedSignatory || 'No Signers'
              return (
                <div className="settings-item">
                  <CheckboxGroupItem
                    disabled
                    label={customerPayment.shortDescription}
                    checked={includes(map(menuType.payments, (payment) => payment.uuid), customerPayment.uuid)}
                    tooltipText={tooltipText}
                  />
                </div>
              )
            })}
          </div>,
        ])
    } else {
      modalData = modalData.concat([
        <div className="settings-row" key={key}>
          <span className="label">No Default Payments Specified</span>
        </div>
      ])
    }

    modalData = modalData.concat([
      <div className="settings-row" key={key}>
        <span className="title">Other</span>
      </div>,
      <div className="settings-row">
        <span className="label">Gratuity</span>
        <span className="value">{gratuity}</span>
      </div>,
      <ToggleWithLabel className="settings-row" disabled label="Gratuity Adjustable" checked={adjustable} />,
      <div className="settings-row">
        <span className="label">Max Gratuity</span>
        <span className="value">{maxGratuity}</span>
      </div>,
    ])

    return {
      title,
      data: modalData
    }
  }

  const menuTypeSettings = map(menuTypes, makeMenuTypeSettings)

  const sections = [
    {
      title: 'Event Settings',
      data: [
        <div className="settings-row">
          <span className="label">Host</span>
          <span className="value">{preference.host}</span>
        </div>,
        <div className="settings-row">
          <span className="label">Receipt</span>
          <span className="value">{preference.receiptEmail}</span>
        </div>,
        <div>
          <ToggleWithLabel className="customer-detail-modal__toggle-with-modal" disabled label="Open Fridge" checked={!preference.lockRefrigerator} />
          <ToggleWithLabel className="customer-detail-modal__toggle-with-modal" disabled label="Open Liquor Cabinet" checked={!preference.lockLiquorCabinet} />
          <ToggleWithLabel className="customer-detail-modal__toggle-with-modal" disabled label="Present Bill" checked={preference.presentBill} />
          <ToggleWithLabel className="customer-detail-modal__toggle-with-modal" disabled label="Print Receipt" checked={preference.provideReceipt} />
        </div>,
        <div className="settings-row block">
          <span className="label">Notes</span>
          <span className="value">{preference.notes || 'None'}</span>
        </div>,
      ]
    },
    {
      title: 'Entitlements',
      data: [
        <div className="settings-row">
          <span className="label">Discount</span>
          <span className="value">{preference.discountDescription}</span>
        </div>,
        <ToggleWithLabel className="settings-row" disabled label="Tax Exempt" checked={preference.taxExempt} />,
      ]
    },
    ...menuTypeSettings,
  ]

  return sections
}

const SuitesCustomerDetails = ({
  customerId,
  customer,
  customerPayments,
  suitesByCustomer,
  currentVenueUuid,
  loadCustomers,
  loadCustomerOrderingPreferences,
  loadCustomerPayments,
  onClose,
}) => {
  const [selectedPreferencesIndex, setSelectedPreferencesIndex] = useState(0)

  useEffect(() => {
    loadCustomers(currentVenueUuid)
  }, [loadCustomers, currentVenueUuid])

  useEffect(() => {
    loadCustomerOrderingPreferences(currentVenueUuid)
  }, [loadCustomerOrderingPreferences, currentVenueUuid])

  useEffect(() => {
    loadCustomerPayments(customerId)
  }, [customerId, loadCustomerPayments])

  const renderEmptyState = () => {
    return (
      <div className="empty-state">
        <h1>Nothing's here</h1>
        <p>It appears there is no information available for {customer.name}.</p>
      </div>
    )
  }

  const renderDropDown = () => {
    if (isEmpty(suitesByCustomer)) return null

    const suitesByCustomerOptions = suitesByCustomer.map((suiteByCustomer, index) => ({
      displayName: `${suiteByCustomer.suite?.name ?? "Default"} preferences`,
      value: index
    }))

    return (
      <div className="section">
        <DropdownMenu
          className="-block"
          name="preferences"
          id="preferences"
          value={selectedPreferencesIndex}
          handleChange={(event) => setSelectedPreferencesIndex(event.target.value)}
          options={suitesByCustomerOptions}
        />
      </div>
    )
  }


  const renderSelectedPreferences = () => {
    if (isEmpty(suitesByCustomer)) return renderEmptyState()

    const viewModel = DefaultPreferencesViewModal(suitesByCustomer[parseInt(selectedPreferencesIndex)], customerPayments)

    return (
      <div>
        {viewModel.map((section, index) => {
          return (
            <div className="section" key={index}>
              <h1>{section.title}</h1>
              {section.data}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <Modal title={customer.name} className="customer-detail-modal" onClose={onClose}>
      {renderDropDown()}
      {renderSelectedPreferences()}
    </Modal>
  )
}

SuitesCustomerDetails.defaultProps = {
  loadCustomers: () => {},
  loadCustomerOrderingPreferences: () => {},
  loadCustomerPayments: () => {}
}

SuitesCustomerDetails.propTypes = {
  loadCustomers: PropTypes.func,
  loadCustomerOrderingPreferences: PropTypes.func,
  loadCustomerPayments: PropTypes.func
}

export default SuitesCustomerDetails
